import React, { useState, useRef, useEffect } from 'react'
import { navigate } from 'gatsby'
import Button from '../components/button'
import InputMask from 'react-input-mask'
import styled, { keyframes } from 'styled-components'

// Spinner animation
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

// Spinner component
const Spinner = styled.svg`
  display: inline-block;
  margin-right: 0.75rem;
  height: 1.25rem;
  width: 1.25rem;
  animation: ${spin} 1s linear infinite;
  vertical-align: middle;

  circle {
    opacity: 0.25;
  }

  path {
    opacity: 0.75;
  }
`

function EstimateForm({
  style,
  showLabels,
  pageTitle,
  pageProps,
  pageLocation,
  showPlaceHolders,
  dark,
}) {
  const [spamFilter, updateSpamFilterStatus] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sessionSource, setSessionSource] = useState('')
  const [url, setUrl] = useState(pageLocation || 'URL not provided')
  const [landingURL, setLandingURL] = useState('')
  const [referrer, setReferrer] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [description, setDescription] = useState('')
  const [errors, updateErrors] = useState([])
  const [botField, setBotField] = useState('')

  // Click IDs for attribution
  const [gclid, setGclid] = useState('')
  const [gbraid, setGbraid] = useState('')
  const [wbraid, setWbraid] = useState('')
  const [fbclid, setFbclid] = useState('')
  const [msclkid, setMsclkid] = useState('')
  const [ttclid, setTtclid] = useState('')
  const [rdt_cid, setRdtCid] = useState('')
  const [epik, setEpik] = useState('')
  const [twclid, setTwclid] = useState('')

  // UTM parameters
  const [utmSource, setUtmSource] = useState('')
  const [utmMedium, setUtmMedium] = useState('')
  const [utmCampaign, setUtmCampaign] = useState('')
  const [utmContent, setUtmContent] = useState('')
  const [utmTerm, setUtmTerm] = useState('')

  // Turnstile refs
  const turnstileContainerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const isInitializedRef = useRef(false)
  const tokenCallbackRef = useRef(null)
  const formInstanceIdRef = useRef(
    `form-${Math.random().toString(36).substr(2, 9)}`
  )

  const TURNSTILE_SITE_KEY = process.env.GATSBY_TURNSTILE_SITE_KEY

  const encode = (data) => {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key])
      )
      .join('&')
  }

  // Cookie helper functions
  const setCookie = (name, value, days) => {
    if (typeof window === 'undefined') return
    const expires = new Date(
      Date.now() + days * 24 * 60 * 60 * 1000
    ).toUTCString()
    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; expires=${expires}; path=/; SameSite=Lax`
  }

  const getCookie = (name) => {
    if (typeof window === 'undefined') return null
    const cookies = document.cookie.split(';')
    for (let cookie of cookies) {
      const [key, value] = cookie.trim().split('=')
      if (key === name) return decodeURIComponent(value)
    }
    return null
  }

  // Initialize Turnstile widget
  useEffect(() => {
    if (typeof window === 'undefined' || isInitializedRef.current) return

    if (!TURNSTILE_SITE_KEY) {
      console.error(
        `[${formInstanceIdRef.current}] Turnstile site key not configured`
      )
      return
    }

    const initTurnstile = () => {
      if (isInitializedRef.current || widgetIdRef.current) {
        console.log(
          `[${formInstanceIdRef.current}] Already initialized, skipping`
        )
        return
      }

      if (!window.turnstile || !turnstileContainerRef.current) {
        console.log(
          `[${formInstanceIdRef.current}] Waiting for Turnstile or container...`
        )
        return
      }

      try {
        console.log(
          `[${formInstanceIdRef.current}] Initializing Turnstile widget...`
        )

        widgetIdRef.current = window.turnstile.render(
          turnstileContainerRef.current,
          {
            sitekey: TURNSTILE_SITE_KEY,
            callback: (token) => {
              console.log(`[${formInstanceIdRef.current}] Token received`)
              if (tokenCallbackRef.current) {
                tokenCallbackRef.current(token)
                tokenCallbackRef.current = null
              }
            },
            'error-callback': (errorCode) => {
              console.error(
                `[${formInstanceIdRef.current}] Turnstile error:`,
                errorCode
              )
              if (tokenCallbackRef.current) {
                tokenCallbackRef.current(null)
                tokenCallbackRef.current = null
              }
            },
            'expired-callback': () => {
              console.log(`[${formInstanceIdRef.current}] Token expired`)
            },
            theme: 'light',
            size: 'invisible',
          }
        )

        isInitializedRef.current = true
        console.log(
          `[${formInstanceIdRef.current}] Widget initialized successfully:`,
          widgetIdRef.current
        )
      } catch (error) {
        console.error(
          `[${formInstanceIdRef.current}] Initialization error:`,
          error
        )
      }
    }

    if (window.turnstile) {
      initTurnstile()
    } else {
      const checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval)
          initTurnstile()
        }
      }, 100)

      const timeout = setTimeout(() => {
        clearInterval(checkInterval)
        console.error(
          `[${formInstanceIdRef.current}] Turnstile script failed to load`
        )
      }, 10000)

      return () => {
        clearInterval(checkInterval)
        clearTimeout(timeout)
      }
    }

    return () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          console.log(`[${formInstanceIdRef.current}] Removing widget`)
          window.turnstile.remove(widgetIdRef.current)
        } catch (e) {
          console.warn(`[${formInstanceIdRef.current}] Cleanup error:`, e)
        }
        widgetIdRef.current = null
        isInitializedRef.current = false
      }
    }
  }, [TURNSTILE_SITE_KEY])

  // Track LAST-TOUCH attribution
  useEffect(() => {
    if (typeof window === 'undefined') return

    const ls = window.localStorage
    const params = new URLSearchParams(window.location.search)
    // Capture referrer ONCE on first page load
const REFERRER_KEY = 'lt_referrer'
if (!ls.getItem(REFERRER_KEY)) {
  const initialReferrer = document.referrer || ''
  if (initialReferrer) {
    ls.setItem(REFERRER_KEY, initialReferrer)
    console.log('[Attribution] Initial referrer saved:', initialReferrer)
  }
}

    // Capture UTM parameters
    const utmParams = {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_term: params.get('utm_term') || '',
    }

  // Derive candidate source
let candidateSource = 'Direct'
const currentHost = window.location.hostname

if (params.has('utm_source')) {
  candidateSource = params.get('utm_source')
} else if (document.referrer) {
  try {
    const refHost = new URL(document.referrer).hostname
    // ✅ Only use referrer if it's NOT your own domain
    if (
      refHost &&
      refHost !== currentHost &&
      !refHost.includes('solidconstructiondesign.com')  // Replace with your actual domain
    ) {
      candidateSource = refHost
    }
  } catch (e) {
    // ignore
  }
}

    // Capture ALL Click IDs
    const clickIds = {
      gclid: params.get('gclid') || '',
      gbraid: params.get('gbraid') || '',
      wbraid: params.get('wbraid') || '',
      fbclid: params.get('fbclid') || '',
      msclkid: params.get('msclkid') || '',
      ttclid: params.get('ttclid') || '',
      rdt_cid: params.get('rdt_cid') || '',
      epik: params.get('epik') || '',
      twclid: params.get('twclid') || '',
    }

    // Check if this is a "quality" visit
    const hasClickId = Object.values(clickIds).some((id) => id !== '')
    const hasUtm = Object.values(utmParams).some((param) => param !== '')
    const hasKnownReferrer = candidateSource !== 'Direct'
    const isQualityVisit = hasClickId || hasUtm || hasKnownReferrer

    console.log('[Attribution] Quality visit check:', {
      hasClickId,
      hasUtm,
      hasKnownReferrer,
      isQualityVisit,
      source: candidateSource,
    })

    const existingCookie = getCookie('lt_attribution')

    if (isQualityVisit) {
      console.log(
        '[Attribution] LAST TOUCH - updating (quality visit detected)'
      )

      const attributionData = {
        gclid: clickIds.gclid,
        gbraid: clickIds.gbraid,
        wbraid: clickIds.wbraid,
        fbclid: clickIds.fbclid,
        msclkid: clickIds.msclkid,
        ttclid: clickIds.ttclid,
        rdt_cid: clickIds.rdt_cid,
        epik: clickIds.epik,
        twclid: clickIds.twclid,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        utm_term: utmParams.utm_term,
        session_source: candidateSource,
        landing_url: window.location.href,
        referrer: ls.getItem(REFERRER_KEY) || '',
        timestamp: Date.now(),
      }

      // Store in localStorage
      Object.keys(clickIds).forEach((key) => {
        if (clickIds[key]) {
          ls.setItem('lt_' + key, clickIds[key])
        } else {
          ls.removeItem('lt_' + key)
        }
      })

      // Only save UTM params if they have values
      if (utmParams.utm_source) {
        ls.setItem('lt_utm_source', utmParams.utm_source)
      } else {
        ls.removeItem('lt_utm_source')
      }
      if (utmParams.utm_medium) {
        ls.setItem('lt_utm_medium', utmParams.utm_medium)
      } else {
        ls.removeItem('lt_utm_medium')
      }
      if (utmParams.utm_campaign) {
        ls.setItem('lt_utm_campaign', utmParams.utm_campaign)
      } else {
        ls.removeItem('lt_utm_campaign')
      }
      if (utmParams.utm_content) {
        ls.setItem('lt_utm_content', utmParams.utm_content)
      } else {
        ls.removeItem('lt_utm_content')
      }
      if (utmParams.utm_term) {
        ls.setItem('lt_utm_term', utmParams.utm_term)
      } else {
        ls.removeItem('lt_utm_term')
      }
  ls.setItem('lt_sessionSource', candidateSource)

      // ADD THIS CODE HERE ⬇️⬇️⬇️
      // Save landing URL on first quality visit only
      const LANDING_URL_KEY = 'lt_landingURL'
      if (!ls.getItem(LANDING_URL_KEY)) {
        ls.setItem(LANDING_URL_KEY, window.location.href)
        console.log('[Attribution] Landing URL saved:', window.location.href)
      }
      // END NEW CODE ⬆️⬆️⬆️

      // Save to cookie (90-day backup)
      setCookie('lt_attribution', JSON.stringify(attributionData), 90)
      console.log('[Attribution] Last-touch updated and saved')
    } else {
      console.log(
        '[Attribution] Skipping update - direct/unknown visit (ad blocker?)'
      )

      // Restore from cookie if localStorage was cleared
      if (!ls.getItem('lt_sessionSource') && existingCookie) {
        console.log('[Attribution] Restoring last-touch from cookie')
        try {
          const savedData = JSON.parse(existingCookie)

          ls.setItem('lt_gclid', savedData.gclid || '')
          ls.setItem('lt_gbraid', savedData.gbraid || '')
          ls.setItem('lt_wbraid', savedData.wbraid || '')
          ls.setItem('lt_fbclid', savedData.fbclid || '')
          ls.setItem('lt_msclkid', savedData.msclkid || '')
          ls.setItem('lt_ttclid', savedData.ttclid || '')
          ls.setItem('lt_rdt_cid', savedData.rdt_cid || '')
          ls.setItem('lt_epik', savedData.epik || '')
          ls.setItem('lt_twclid', savedData.twclid || '')
          ls.setItem('lt_utm_source', savedData.utm_source || '')
          ls.setItem('lt_utm_medium', savedData.utm_medium || '')
          ls.setItem('lt_utm_campaign', savedData.utm_campaign || '')
          ls.setItem('lt_utm_content', savedData.utm_content || '')
          ls.setItem('lt_utm_term', savedData.utm_term || '')
          ls.setItem('lt_sessionSource', savedData.session_source || '')
          ls.setItem('lt_landingURL', savedData.landing_url || '')
          if (savedData.referrer) {
  ls.setItem(REFERRER_KEY, savedData.referrer)
}
          console.log('[Attribution] Last-touch restored from cookie')
        } catch (e) {
          console.error('[Attribution] Failed to parse cookie:', e)
        }
      }
    }

    // Set state from localStorage
    setGclid(ls.getItem('lt_gclid') || '')
    setGbraid(ls.getItem('lt_gbraid') || '')
    setWbraid(ls.getItem('lt_wbraid') || '')
    setFbclid(ls.getItem('lt_fbclid') || '')
    setMsclkid(ls.getItem('lt_msclkid') || '')
    setTtclid(ls.getItem('lt_ttclid') || '')
    setRdtCid(ls.getItem('lt_rdt_cid') || '')
    setEpik(ls.getItem('lt_epik') || '')
    setTwclid(ls.getItem('lt_twclid') || '')
    setUtmSource(ls.getItem('lt_utm_source') || '')
    setUtmMedium(ls.getItem('lt_utm_medium') || '')
    setUtmCampaign(ls.getItem('lt_utm_campaign') || '')
    setUtmContent(ls.getItem('lt_utm_content') || '')
    setUtmTerm(ls.getItem('lt_utm_term') || '')
    setSessionSource(ls.getItem('lt_sessionSource') || 'Direct')
    setLandingURL(ls.getItem('lt_landingURL') || window.location.href) 
    setReferrer(ls.getItem(REFERRER_KEY) || '')
    setUrl(window.location.href)
  }, [])

  const getTurnstileToken = () => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Not in browser environment'))
        return
      }

      if (!window.turnstile || widgetIdRef.current === null) {
        reject(new Error('Turnstile not initialized'))
        return
      }

      console.log(`[${formInstanceIdRef.current}] Requesting token...`)

      tokenCallbackRef.current = (token) => {
        if (token) {
          console.log(
            `[${formInstanceIdRef.current}] Token obtained successfully`
          )
          resolve(token)
        } else {
          console.error(
            `[${formInstanceIdRef.current}] Token verification failed`
          )
          reject(new Error('Turnstile verification failed'))
        }
      }

      const timeout = setTimeout(() => {
        if (tokenCallbackRef.current) {
          tokenCallbackRef.current = null
          console.error(
            `[${formInstanceIdRef.current}] Token request timed out`
          )
          reject(new Error('Turnstile verification timed out'))
        }
      }, 30000)

      try {
        window.turnstile.execute(widgetIdRef.current)
      } catch (error) {
        clearTimeout(timeout)
        tokenCallbackRef.current = null
        console.error(`[${formInstanceIdRef.current}] Execute error:`, error)
        reject(error)
      }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Spam filter check
    if (
      phone.includes('(171') ||
      phone.includes('(191') ||
      phone.includes('(121')
    ) {
      updateSpamFilterStatus(true)
      return false
    }

    // Prevent double submission
    if (isSubmitting) {
      console.log(`[${formInstanceIdRef.current}] Already submitting, ignoring`)
      return false
    }

    setIsSubmitting(true)

    try {
      console.log(`[${formInstanceIdRef.current}] Starting form submission...`)

      // Get Turnstile token
      const turnstileToken = await getTurnstileToken()

      // Prepare form data
      const formData = {
        'form-name': 'free-estimate-form',
        subject: `SOLIDCD Form Submission - ${firstName} ${lastName}`,
        firstname: firstName,
        lastname: lastName,
        url: url,
        landingURL: landingURL,
        email: email,
        phone: phone,
        description: description,
        referrer: referrer,
        ['bot-field']: botField,
        session_source: sessionSource,
        gclid: gclid,
        gbraid: gbraid,
        wbraid: wbraid,
        fbclid: fbclid,
        msclkid: msclkid,
        ttclid: ttclid,
        rdt_cid: rdt_cid,
        epik: epik,
        twclid: twclid,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
        utm_term: utmTerm,
      }

      console.log(`[${formInstanceIdRef.current}] Verifying with Turnstile...`)

      // Submit to Netlify Function for verification
      const response = await fetch('/.netlify/functions/verify-turnstile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: turnstileToken,
          formData: formData,
        }),
      })

      const result = await response.json()

      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = result.retryAfter || 60
        alert(
          `You've submitted too many forms recently. Please wait ${retryAfter} seconds and try again.`
        )
        setIsSubmitting(false)
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current)
        }
        return
      }

      // Check if verification passed
      if (!response.ok || !result.success) {
        console.error(
          `[${formInstanceIdRef.current}] Verification failed:`,
          result.error
        )
        throw new Error(result.error || 'Verification failed')
      }

      console.log(`[${formInstanceIdRef.current}] Form submitted successfully!`)

      // Navigate to thank you page
      setTimeout(
        () =>
          navigate('/thanks', {
            state: {
              firstname: firstName,
              lastName: lastName,
              email: email,
            },
          }),
        400
      )
    } catch (error) {
      console.error(`[${formInstanceIdRef.current}] Submission error:`, error)

      alert(
        'We had trouble verifying your submission. Please try again or give us a call.'
      )

      setIsSubmitting(false)

      // Reset Turnstile widget
      if (
        typeof window !== 'undefined' &&
        window.turnstile &&
        widgetIdRef.current !== null
      ) {
        try {
          window.turnstile.reset(widgetIdRef.current)
          console.log(`[${formInstanceIdRef.current}] Widget reset`)
        } catch (resetError) {
          console.error(
            `[${formInstanceIdRef.current}] Reset error:`,
            resetError
          )
        }
      }
    }
  }

  return (
    <form
      id="free-estimate-form"
      onSubmit={(e) => handleSubmit(e)}
      name="free-estimate-form"
      className={
        style
          ? style + ' mt-4 mb-0 mx-auto'
          : 'contact-form-default-style mt-4 mb-0 mx-auto'
      }
    >
      {/* Hidden fields */}
      <input
        type="hidden"
        name="bot-field"
        value={botField}
        onChange={(e) => setBotField(e.target.value)}
      />
      <input type="hidden" name="form-name" value="free-estimate-form" />
      <input type="hidden" name="url" value={url} />
      <input type="hidden" name="landingURL" value={landingURL} />
      <input
        type="hidden"
        name="referrer"
        value={referrer}

      />
      <input type="hidden" id="subject" name="subject" />
      
      {/* LAST-TOUCH attribution fields */}
      <input type="hidden" name="session_source" value={sessionSource} />
      <input type="hidden" name="gclid" value={gclid} />
      <input type="hidden" name="gbraid" value={gbraid} />
      <input type="hidden" name="wbraid" value={wbraid} />
      <input type="hidden" name="fbclid" value={fbclid} />
      <input type="hidden" name="msclkid" value={msclkid} />
      <input type="hidden" name="ttclid" value={ttclid} />
      <input type="hidden" name="rdt_cid" value={rdt_cid} />
      <input type="hidden" name="epik" value={epik} />
      <input type="hidden" name="twclid" value={twclid} />
      <input type="hidden" name="utm_source" value={utmSource} />
      <input type="hidden" name="utm_medium" value={utmMedium} />
      <input type="hidden" name="utm_campaign" value={utmCampaign} />
      <input type="hidden" name="utm_content" value={utmContent} />
      <input type="hidden" name="utm_term" value={utmTerm} />

      {/* Invisible Turnstile container */}
      {typeof window !== 'undefined' && (
        <div ref={turnstileContainerRef} style={{ display: 'none' }}></div>
      )}

      {/* No JavaScript fallback */}
      <noscript>
        <div
          className="bg-yellow-50 border border-yellow-200 p-4 rounded-md mb-4"
          style={{
            backgroundColor: '#fefce8',
            border: '1px solid #fef08a',
            padding: '1rem',
            borderRadius: '0.375rem',
            marginBottom: '1rem',
          }}
        >
          <p
            style={{
              color: '#854d0e',
              fontWeight: 600,
              marginBottom: '0.5rem',
            }}
          >
            JavaScript is required to submit this form.
          </p>
          <p style={{ color: '#a16207', marginTop: '0.5rem' }}>
            Please call us directly or enable JavaScript in your browser.
          </p>
        </div>
      </noscript>

      <div className="grid grid-cols-1 row-gap-3 col-gap-4 sm:grid-cols-6">
        <div className="sm:col-span-3">
          <label
            htmlFor="firstname"
            className={`block text-base font-medium leading-5 ${
              dark ? 'text-white' : 'text-gray-800'
            } ${!showLabels ? 'sr-only' : ''}`}
          >
            First Name
          </label>
          <div className="mt-1">
            <input
              id="firstname"
              name="firstname"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={showPlaceHolders ? 'First Name' : ''}
              className="form-control  "
              required={true}
            />
          </div>
        </div>

        <div className="sm:col-span-3">
          <label
            htmlFor="lastname"
            className={`block text-base font-medium leading-5 ${
              dark ? 'text-white' : 'text-gray-800'
            } ${!showLabels ? 'sr-only' : ''}`}
          >
            Last Name
          </label>
          <div className="mt-1">
            <input
              id="lastname"
              name="lastname"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="form-control"
              placeholder={showPlaceHolders ? 'Last Name' : ''}
              required={true}
            />
          </div>
        </div>

        <div className="sm:col-span-3">
          <label
            htmlFor="email"
            className={`block text-base font-medium leading-5 ${
              dark ? 'text-white' : 'text-gray-800'
            } ${!showLabels ? 'sr-only' : ''}`}
          >
            Email address
          </label>
          <div className="mt-1">
            <input
              type="email"
              name="email"
              id="email"
              value={email}
              placeholder={showPlaceHolders ? 'Email' : ''}
              required={true}
              onChange={(e) => {
                setEmail(e.target.value)
                if (
                  e.target.value.match(
                    /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
                  ) == null
                ) {
                  if (typeof e.target.setCustomValidity !== 'function') {
                    updateErrors([
                      ...errors.filter(([field]) => field !== 'email'),
                      [
                        'email',
                        'Please enter a valid domain at the end of your email address',
                      ],
                    ])
                  } else {
                    e.target.setCustomValidity(
                      'Please enter a valid domain at the end of your email address'
                    )
                  }
                } else {
                  e.target.setCustomValidity('')
                  if (typeof e.target.setCustomValidity !== 'function')
                    updateErrors([
                      ...errors.filter(([field]) => field !== 'email'),
                    ])
                }
              }}
              className="form-control "
            />
          </div>
          {errors.filter(([field]) => field === 'email')[0] && (
            <div className="text-red-700">
              {errors.filter(([field]) => field === 'email')[0][1]}
            </div>
          )}
        </div>

        <div className="sm:col-span-3">
          <label
            htmlFor="phone"
            className={`block text-base font-medium leading-5 ${
              dark ? 'text-white' : 'text-gray-800'
            } ${!showLabels ? 'sr-only' : ''}`}
          >
            Phone
          </label>
          <div className="mt-1">
            <InputMask
              type="tel"
              name="phone"
              id="phone"
              value={phone}
              placeholder={showPlaceHolders ? 'Phone' : ''}
              className="form-control "
              mask="(999) 999-9999"
              onChange={(e) => {
                setPhone(e.target.value)
                if (
                  e.target.value.match(
                    /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/
                  ) == null
                ) {
                  if (typeof e.target.setCustomValidity !== 'function') {
                    updateErrors([
                      ...errors.filter(([field]) => field !== 'phone'),
                      ['phone', 'Please enter a complete phone number'],
                    ])
                  } else {
                    e.target.setCustomValidity(
                      'Please enter a complete phone number'
                    )
                  }
                } else {
                  e.target.setCustomValidity('')
                  if (typeof e.target.setCustomValidity !== 'function')
                    updateErrors([
                      ...errors.filter(([field]) => field !== 'phone'),
                    ])
                }
              }}
              required
            />
          </div>
          {errors.filter(([field]) => field === 'phone')[0] && (
            <div className="text-red-700">
              {errors.filter(([field]) => field === 'phone')[0][1]}
            </div>
          )}
        </div>

        <div className="sm:col-span-6">
          <label
            htmlFor="description"
            className={`block text-base font-medium leading-5 ${
              dark ? 'text-white' : 'text-gray-800'
            } ${!showLabels ? 'sr-only' : ''}`}
          >
            Tell us about your project
          </label>
          <div className="mt-1">
            <textarea
              id="description"
              name="description"
              type="text"
              rows="4"
              value={description}
              placeholder={
                showPlaceHolders ? 'Tell us about your project' : ''
              }
              onChange={(e) => setDescription(e.target.value)}
              className=" form-control "
            ></textarea>
          </div>{' '}
        </div>
      </div>

      <div className="border-t border-gray-200 mt-3 w-full">
        {spamFilter && (
          <div className="font-bold bg-red-600 p-2 px-4 mb-2 text-white">
            An Error Occurred. Give Us A Call.
          </div>
        )}
        <Button
          role="submit"
          type="submit"
          color="primary"
          className="shadow-lg w-full"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </Spinner>
              Submitting...
            </>
          ) : (
            'Get FREE Estimate →'
          )}
        </Button>
      </div>

      <div
        className={[
          'flex items-center justify-center mt-2 formdisclosure',
          dark ? 'text-white' : 'text-gray-700',
        ].join(' ')}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-4 h-4 mr-1"
          style={dark ? null : { color: '#4b5767' }}
        >
          <path
            fillRule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clipRule="evenodd"
          />
        </svg>
        <span className={dark ? 'text-white' : null}>
          *Free estimates for property owners/managers. Real estate
          evaluations/inspections are billable.
        </span>
      </div>
    </form>
  )
}

export default EstimateForm